"use client";

import { useState, useEffect } from "react";
import { Loader2, Check, Circle } from "lucide-react";

interface AnalysisStep {
  id: string;
  title: string;
  status: "pending" | "loading" | "completed";
}

interface AnalysisProgressProps {
  isVisible: boolean;
  onComplete?: () => void;
  waitForDone?: boolean;
  done?: boolean;
}

export function AnalysisProgress({
  isVisible,
  onComplete,
  waitForDone = false,
  done = false,
}: AnalysisProgressProps) {
  const [steps, setSteps] = useState<AnalysisStep[]>([
    { id: "site-metadata", title: "Analyzing Site", status: "pending" },
    {
      id: "domain-rating",
      title: "Checking Domain Rating",
      status: "pending",
    },
    {
      id: "domain-analysis",
      title: "Scanning Security",
      status: "pending",
    },
    {
      id: "chrome-ux",
      title: "Measuring UX",
      status: "pending",
    },
    {
      id: "pagespeed",
      title: "Testing Speed",
      status: "pending",
    },
  ]);

  // Reset steps when visibility changes
  useEffect(() => {
    if (isVisible) {
      setSteps([
        { id: "site-metadata", title: "Analyzing Site", status: "pending" },
        {
          id: "domain-rating",
          title: "Checking Domain Rating",
          status: "pending",
        },
        {
          id: "domain-analysis",
          title: "Scanning Security",
          status: "pending",
        },
        {
          id: "chrome-ux",
          title: "Measuring UX",
          status: "pending",
        },
        {
          id: "pagespeed",
          title: "Testing Speed",
          status: "pending",
        },
      ]);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const processStep = (stepIndex: number) => {
      // If we're at or past the last step
      if (stepIndex >= steps.length - 1) {
        // Set last step to loading
        setSteps((prev) => {
          const next = [...prev];
          next[steps.length - 1].status = "loading";
          if (steps.length - 2 >= 0) {
            next[steps.length - 2].status = "completed";
          }
          return next;
        });

        // If we don't need to wait, complete after short delay
        if (!waitForDone) {
          setTimeout(() => onComplete?.(), 1000);
        }
        return;
      }

      // Mark current step as loading
      setSteps((prevSteps) => {
        const newSteps = [...prevSteps];
        newSteps[stepIndex].status = "loading";
        return newSteps;
      });

      // Mark previous step as completed
      if (stepIndex > 0) {
        setSteps((prevSteps) => {
          const newSteps = [...prevSteps];
          newSteps[stepIndex - 1].status = "completed";
          return newSteps;
        });
      }

      // Calculate delay:1,5 seconds for step 1, 3 seconds for step 2, etc.
      const delay = (stepIndex + 1) * 1500;

      setTimeout(() => {
        processStep(stepIndex + 1);
      }, delay);
    };

    processStep(0);
  }, [isVisible, steps.length, onComplete, waitForDone]);

  // When external 'done' is signaled, finish the last step and fire onComplete
  useEffect(() => {
    if (!isVisible || !waitForDone) return;
    if (!done) return;

    setSteps((prev) => {
      const next = [...prev];
      next[next.length - 1].status = "completed";
      return next;
    });
    setTimeout(() => onComplete?.(), 300);
  }, [done, isVisible, waitForDone, onComplete]);

  if (!isVisible) return null;

  return (
    <div className="w-full mx-auto mt-10 max-w-sm">
      <div className="space-y-4">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`flex items-center gap-4 transition-all duration-700 ease-out transform ${
              step.status === "loading"
                ? "opacity-100 scale-100 translate-x-0"
                : step.status === "completed"
                ? "opacity-70 scale-98 translate-x-0"
                : "opacity-40 scale-95 translate-x-0"
            }`}
            style={{
              transitionDelay:
                step.status === "loading" ? "0ms" : `${index * 100}ms`,
            }}
          >
            {/* Icon Container - Zentriert */}
            <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
              {step.status === "loading" ? (
                <div className="relative">
                  <div className="w-6 h-6 rounded-full bg-primary/20 animate-pulse" />
                  <Loader2 className="w-4 h-4 animate-spin text-primary absolute top-1 left-1" />
                </div>
              ) : step.status === "completed" ? (
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center animate-in zoom-in duration-300"
                  style={{ backgroundColor: "#10b981" }}
                >
                  <Check className="w-4 h-4 text-white" />
                </div>
              ) : (
                <Circle className="w-6 h-6 text-muted-foreground/40" />
              )}
            </div>

            {/* Text - Linksbündig */}
            <div className="flex-1">
              <div
                className={`text-base transition-all duration-500 ${
                  step.status === "loading"
                    ? "text-foreground font-semibold"
                    : step.status === "completed"
                    ? "text-foreground font-medium"
                    : "text-muted-foreground/60 font-normal"
                }`}
              >
                {step.title}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
